import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';

// Components
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import DashboardHub from './components/DashboardHub';
import BrandManagement from './components/BrandManagement';
import VendorManagement from './components/VendorManagement';
import WarehouseManagement from './components/WarehouseManagement';
import ProductManagement from './components/ProductManagement';
import UserManagement from './components/UserManagement';
import PurchaseOrderManagement from './components/PurchaseOrderManagement';
import PurchaseEntryManagement from './components/PurchaseEntryManagement';
import PurchaseEntryDashboard from './components/PurchaseEntryDashboard';
import PurchaseReturnManagement from './components/PurchaseReturnManagement';
import PODashboard from './components/PODashboard';
import VendorAnalytics from './components/VendorAnalytics';
import AgentManagement from './components/AgentManagement';
import AgentSalesByParty from './components/AgentSalesByParty';
import DistributorManagement from './components/DistributorManagement';
import CustomerManagement from './components/CustomerManagement';
import SalesOrderManagement from './components/SalesOrderManagement';
import SalesEntryManagement from './components/SalesEntryManagement';
import SODashboard from './components/SODashboard';
import GeoDashboard from './components/GeoDashboard';
import Reports from './components/Reports';
import OperationsHub from './components/OperationsHub';
import SalesReturnManagement from './components/SalesReturnManagement';
import DistributorDispatch from './components/DistributorDispatch';
import WarehouseTransfer from './components/WarehouseTransfer';
import ReadyToDispatch from './components/ReadyToDispatch';
import StockAudit from './components/StockAudit';
import Approvals from './components/Approvals';
import Shortfall from './components/Shortfall';
import AuditLog from './components/AuditLog';
import SecurityDashboard from './components/SecurityDashboard';
import SalesTargets from './components/SalesTargets';
import DailyExpenses from './components/DailyExpenses';
import Catalogue from './components/Catalogue';
import DailySummary from './components/DailySummary';
import DeadStock from './components/DeadStock';
import DefectGoods from './components/DefectGoods';
import DesignPerformance from './components/DesignPerformance';
import AdminTools from './components/AdminTools';
import AgentCommission from './components/AgentCommission';
import FinancialStatements from './components/FinancialStatements';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import ArchivePage from './components/ArchivePage';
import WatermarkOverlay from './components/WatermarkOverlay';
import { useScreenshotBlock } from './hooks/useScreenshotBlock';
import CreateSalesOrder from './components/CreateSalesOrder';
import EventDashboard from './components/EventDashboard';
import SampleTracker from './components/SampleTracker';
import CreateSalesEntry from './components/CreateSalesEntry';
import CreatePurchaseEntry from './components/CreatePurchaseEntry';
import CreatePurchaseReturn from './components/CreatePurchaseReturn';
import CreateSalesReturn from './components/CreateSalesReturn';
import PurchaseOrderDetail from './components/PurchaseOrderDetail';
import VendorStockDetail from './components/VendorStockDetail';
import PurchaseEntryDetail from './components/PurchaseEntryDetail';
import SalesOrderDetail from './components/SalesOrderDetail';
import ProcessSOItems from './components/ProcessSOItems';
import SalesEntryDetail from './components/SalesEntryDetail';
import SalesReturnDetail from './components/SalesReturnDetail';
import PurchaseReturnDetail from './components/PurchaseReturnDetail';
import CreatePurchaseOrder from './components/CreatePurchaseOrder';
import LedgerReport from './components/LedgerReport';
import LedgerDetail from './components/LedgerDetail';
import Sidebar from './components/Sidebar';
import { Toaster } from './components/ui/sonner';
import { useKeyboardShortcuts, ShortcutHelpOverlay } from './hooks/useKeyboardShortcuts';
import { logAudit } from './lib/audit';
import api from './lib/api';

const PageViewLogger = () => {
  const location = useLocation();
  useEffect(() => {
    logAudit('page_view', location.pathname);
  }, [location.pathname]);
  return null;
};

const HeartbeatPing = ({ user }) => {
  useEffect(() => {
    if (!user) return;
    const ping = () => api.post('/security/heartbeat').catch(() => {});
    ping();
    const id = setInterval(ping, 30000); // every 30s
    return () => clearInterval(id);
  }, [user]);
  return null;
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);

  useKeyboardShortcuts({
    onHelp: () => setShowShortcutHelp(prev => !prev),
    onEscape: () => setShowShortcutHelp(false),
  });

  // Block screenshots for non-super-admin users
  useScreenshotBlock(user);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Garment360 ERP...</h2>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Toaster position="top-right" />
        <ShortcutHelpOverlay open={showShortcutHelp} onClose={() => setShowShortcutHelp(false)} />
        {!user ? (
          <Routes>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        ) : (
          <div className="flex h-screen bg-gray-50">
            <Sidebar user={user} onLogout={handleLogout} />
            <main className="flex-1 overflow-y-auto">
              <WatermarkOverlay code={(user?.role === 'admin' || user?.role === 'super_admin') ? null : user?.watermark_code} />
              <PageViewLogger />
              <HeartbeatPing user={user} />
              <Routes>
                <Route path="/" element={user?.role === 'super_admin' ? <SuperAdminDashboard user={user} /> : <DashboardHub user={user} />} />
                <Route path="/brands" element={<BrandManagement user={user} />} />
                <Route path="/vendors" element={<VendorManagement user={user} />} />
                <Route path="/warehouses" element={<WarehouseManagement user={user} />} />
                <Route path="/products" element={<ProductManagement user={user} />} />
                <Route path="/purchase-orders" element={<PurchaseOrderManagement user={user} />} />
                <Route path="/purchase-order/:id" element={<PurchaseOrderDetail user={user} />} />
                <Route path="/create-purchase-order" element={<CreatePurchaseOrder user={user} />} />
                <Route path="/purchase-entry" element={<PurchaseEntryManagement user={user} />} />
                <Route path="/purchase-entry/:id" element={<PurchaseEntryDetail user={user} />} />
                <Route path="/create-purchase-entry" element={<CreatePurchaseEntry user={user} />} />
                <Route path="/pe-dashboard" element={<Navigate to="/?tab=pe" replace />} />
                <Route path="/purchase-returns" element={<PurchaseReturnManagement user={user} />} />
                <Route path="/purchase-return/:id" element={<PurchaseReturnDetail user={user} />} />
                <Route path="/create-purchase-return" element={<CreatePurchaseReturn user={user} />} />
                <Route path="/po-dashboard" element={<Navigate to="/?tab=po" replace />} />
                <Route path="/vendor-analytics" element={<VendorAnalytics user={user} />} />
                <Route path="/vendor-stock/:vendorId" element={<VendorStockDetail user={user} />} />
                <Route path="/agents" element={<AgentManagement user={user} />} />
                <Route path="/agents/:agentId/sales" element={<AgentSalesByParty user={user} />} />
                <Route path="/distributors" element={<DistributorManagement user={user} />} />
                <Route path="/customers" element={<CustomerManagement user={user} />} />
                <Route path="/sales-orders" element={<SalesOrderManagement user={user} />} />
                <Route path="/event-dashboard" element={<EventDashboard />} />
                <Route path="/sample-tracker" element={<SampleTracker user={user} />} />
                <Route path="/sales-order/:id" element={<SalesOrderDetail user={user} />} />
                <Route path="/sales-order/:id/process-items" element={<ProcessSOItems user={user} />} />
                <Route path="/create-sales-order" element={<CreateSalesOrder user={user} />} />
                <Route path="/sales-entry" element={<SalesEntryManagement user={user} />} />
                <Route path="/sales-entry/:id" element={<SalesEntryDetail user={user} />} />
                <Route path="/create-sales-entry" element={<CreateSalesEntry user={user} />} />
                <Route path="/sales-returns" element={<SalesReturnManagement user={user} />} />
                <Route path="/sales-return/:id" element={<SalesReturnDetail user={user} />} />
                <Route path="/create-sales-return" element={<CreateSalesReturn user={user} />} />
                <Route path="/distributor-dispatch" element={<DistributorDispatch user={user} />} />
                <Route path="/warehouse-transfers" element={<WarehouseTransfer user={user} />} />
                <Route path="/ready-to-dispatch" element={<ReadyToDispatch user={user} />} />
                <Route path="/stock-audit" element={<StockAudit user={user} />} />
                <Route path="/approvals" element={<Approvals user={user} />} />
                <Route path="/shortfall" element={<Shortfall user={user} />} />
                <Route path="/catalogue" element={<Catalogue user={user} />} />
                <Route path="/daily-summary" element={<DailySummary user={user} />} />
                <Route path="/dead-stock" element={<DeadStock user={user} />} />
                <Route path="/defect-goods" element={<DefectGoods user={user} />} />
                <Route path="/design-performance" element={<DesignPerformance user={user} />} />
                <Route path="/so-dashboard" element={<Navigate to="/?tab=so" replace />} />
                <Route path="/geo-dashboard" element={<Navigate to="/?tab=geo" replace />} />
                <Route path="/reports" element={<Reports user={user} />} />
                <Route path="/ledger-report" element={<LedgerReport user={user} />} />
                <Route path="/ledger/:type/:id" element={<LedgerDetail user={user} />} />
                <Route path="/operations" element={<OperationsHub user={user} />} />
                <Route path="/users" element={<UserManagement user={user} />} />
                <Route path="/audit-log" element={<AuditLog user={user} />} />
                <Route path="/security" element={<SecurityDashboard user={user} />} />
                <Route path="/sales-targets" element={<SalesTargets user={user} />} />
                <Route path="/expenses" element={<DailyExpenses user={user} />} />
                <Route path="/admin-tools" element={<AdminTools user={user} />} />
                <Route path="/agent-commission" element={<AgentCommission user={user} />} />
                <Route path="/financial-statements" element={<FinancialStatements user={user} />} />
                <Route path="/archive" element={<ArchivePage user={user} />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </main>
          </div>
        )}
      </div>
    </Router>
  );
}

export default App;